import React, { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Play, Eye, Loader, ChevronUp, ChevronDown, Cpu, Zap } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Spinner = styled(Loader)`animation: ${spin} 1s linear infinite;`;

const Container = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #11131c;
  display: flex;
  flex-direction: column;
  max-height: ${p => p.$expanded ? '50vh' : '44px'};
  transition: max-height 0.3s ease;
  overflow: hidden;
`;

const ToggleBar = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: none;
  border: none;
  color: #888;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  text-align: left;
  flex-shrink: 0;
  &:hover { color: #aaa; }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const VarGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

const VarLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #f59e0b;
  margin-bottom: 4px;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
`;

const VarInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 12px;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  padding: 8px 10px;
  resize: vertical;
  &:focus { outline: none; border-color: rgba(245, 158, 11, 0.5); }
  &::placeholder { color: #444; }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ExampleControlsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s;
  border: none;
`;

const PreviewBtn = styled(ActionBtn)`
  background: rgba(255, 255, 255, 0.08);
  color: #ccc;
  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.12); }
`;

const RunBtn = styled(ActionBtn)`
  background: ${p => p.disabled ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.8)'};
  color: ${p => p.disabled ? '#666' : '#fff'};
  &:hover:not(:disabled) { background: rgba(34, 197, 94, 1); }
`;

const ResultSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResultLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ResultBox = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 12px;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #ccc;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
`;

const UsageBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 6px;
  font-size: 11px;
  color: #a5b4fc;
`;

const ErrorBox = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  color: #f87171;
`;

const SmallSelect = styled.select`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  padding: 8px 10px;
  font-size: 12px;
  min-width: 220px;
  max-width: 100%;
`;

const MetaInfo = styled.div`
  font-size: 11px;
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  padding: 8px 10px;
`;

function extractVariables(text) {
  if (!text) return [];
  const matches = text.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))].filter(v => v !== 'if' && v !== '/if');
}

function renderTemplate(templateString, variables) {
  if (!templateString) return '';
  let result = templateString;
  result = result.replace(
    /\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, varName, block) => {
      const val = variables[varName];
      if (val === undefined || val === null || val === '' || val === false) return '';
      return block;
    }
  );
  result = result.replace(
    /\{\{(\w+)\}\}/g,
    (_, varName) => {
      const val = variables[varName];
      if (val === undefined || val === null) return '';
      return String(val);
    }
  );
  return result;
}

function composeSystemPrompt(technicalPrompt, qualitativePrompt, fallbackPrompt = '') {
  const parts = [];
  const tech = String(technicalPrompt || '').trim();
  const qual = String(qualitativePrompt || '').trim();
  if (tech) parts.push(`# Core Rules\n\n${tech}`);
  if (qual) parts.push(`# Domain Guidance\n\n${qual}`);
  if (parts.length > 1) {
    parts.push('# Precedence\n\nIf Core Rules and Domain Guidance conflict, Core Rules take priority.');
  }
  const combined = parts.join('\n\n');
  return combined || fallbackPrompt || '';
}

// scope 'instance' (Valmistelu › Promptit, #705): run the row this instance
// runs, its own version or else the global one, with this instance's data only.
const PromptTestPanel = ({ template, instanceId, scope = 'global' }) => {
  const [expanded, setExpanded] = useState(false);
  const [varValues, setVarValues] = useState({});
  const [selectedInstanceId, setSelectedInstanceId] = useState(instanceId || '');
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingExample, setLoadingExample] = useState(false);
  const [exampleMeta, setExampleMeta] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const variables = useMemo(
    () => extractVariables(
      composeSystemPrompt(
        template?.system_prompt_technical ?? template?.system_prompt ?? '',
        template?.system_prompt_qualitative || '',
        template?.system_prompt || ''
      ) + (template?.user_prompt_template || '')
    ),
    [template?.system_prompt, template?.system_prompt_technical, template?.system_prompt_qualitative, template?.user_prompt_template]
  );

  useEffect(() => {
    setSelectedInstanceId(instanceId || '');
  }, [instanceId]);

  useEffect(() => {
    let cancelled = false;
    const loadInstances = async () => {
      setLoadingInstances(true);
      try {
        const res = await fetch(API_ENDPOINTS.INSTANCES());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setInstances(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setInstances([]);
      } finally {
        if (!cancelled) setLoadingInstances(false);
      }
    };
    loadInstances();
    return () => { cancelled = true; };
  }, []);

  const handlePreview = () => {
    if (!template) return;
    const rendered = {
      system: renderTemplate(
        composeSystemPrompt(
          template.system_prompt_technical ?? template.system_prompt ?? '',
          template.system_prompt_qualitative || '',
          template.system_prompt || ''
        ),
        varValues
      ),
      user: renderTemplate(template.user_prompt_template, varValues),
    };
    setPreview(rendered);
    setResult(null);
    setError(null);
  };

  const handleRun = async () => {
    if (!template || running) return;
    setRunning(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT_TEST(template.key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ variables: varValues, instanceId: selectedInstanceId || null, scope }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setResult(data);
      setPreview({ system: data.renderedSystem, user: data.renderedUser });
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleLoadExampleData = async () => {
    if (!template || !selectedInstanceId || loadingExample) return;
    setLoadingExample(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT_EXAMPLE_DATA(template.key, {
        instanceId: selectedInstanceId,
        includeRag: true,
      }), { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setVarValues(prev => ({ ...prev, ...(data.variables || {}) }));
      setExampleMeta(data.meta || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingExample(false);
    }
  };

  return (
    <Container $expanded={expanded}>
      <ToggleBar onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        <Zap size={14} /> Testaa promptia
        {result && (result.usage?.input_tokens != null || result.usage?.output_tokens != null) && (
          <UsageBadge>
            <Cpu size={10} /> {(result.usage?.input_tokens ?? 0) + (result.usage?.output_tokens ?? 0)} tokenia
          </UsageBadge>
        )}
      </ToggleBar>

      <Content>
        <ExampleControlsRow>
          {scope !== 'instance' && <SmallSelect
            value={selectedInstanceId}
            onChange={(e) => setSelectedInstanceId(e.target.value)}
            disabled={loadingInstances}
          >
            <option value="">{loadingInstances ? 'Ladataan instansseja...' : 'Testidata: valitse instanssi'}</option>
            {instances.map(inst => (
              <option key={inst.id} value={inst.id}>
                {inst.title || inst.id}
              </option>
            ))}
          </SmallSelect>}
          <PreviewBtn onClick={handleLoadExampleData} disabled={!template || !selectedInstanceId || loadingExample}>
            {loadingExample ? <Spinner size={14} /> : <Zap size={14} />}
            {loadingExample ? 'Ladataan...' : 'Lataa esimerkkidata'}
          </PreviewBtn>
        </ExampleControlsRow>

        {variables.length > 0 && (
          <Row>
            {variables.map(v => (
              <VarGroup key={v}>
                <VarLabel>{'{{' + v + '}}'}</VarLabel>
                <VarInput
                  value={varValues[v] || ''}
                  onChange={e => setVarValues(prev => ({ ...prev, [v]: e.target.value }))}
                  placeholder={`Syötä ${v}...`}
                />
              </VarGroup>
            ))}
          </Row>
        )}

        <ButtonRow>
          <PreviewBtn onClick={handlePreview} disabled={!template}>
            <Eye size={14} /> Esikatsele
          </PreviewBtn>
          <RunBtn onClick={handleRun} disabled={!template || running}>
            {running ? <Spinner size={14} /> : <Play size={14} />}
            {running ? 'Ajetaan...' : 'Aja testi'}
          </RunBtn>
        </ButtonRow>

        {error && <ErrorBox>{error}</ErrorBox>}
        {exampleMeta && (
          <MetaInfo>
            Lähde: {exampleMeta.instanceTitle || exampleMeta.instanceId || '-'}
            {exampleMeta.sampleScenarioTitle ? ` · skenaario: ${exampleMeta.sampleScenarioTitle}` : ''}
            {exampleMeta.sampleNodeId ? ` · solmu: ${exampleMeta.sampleNodeId}` : ''}
            {exampleMeta.sampleSolutionId ? ` · ratkaisu: ${exampleMeta.sampleSolutionId}` : ''}
          </MetaInfo>
        )}

        {preview && (
          <ResultSection>
            <ResultLabel>Renderöity system-prompt</ResultLabel>
            <ResultBox>{preview.system || '(tyhjä)'}</ResultBox>
            <ResultLabel>Renderöity user-prompt</ResultLabel>
            <ResultBox>{preview.user || '(tyhjä)'}</ResultBox>
          </ResultSection>
        )}

        {result && (
          <ResultSection>
            <ResultLabel>
              AI-vastaus
              {(result.styleApplied || result.inlineStyleApplied) && (
                <span style={{ fontWeight: 400, color: '#a5b4fc', marginLeft: 8, textTransform: 'none' }}>
                  (kieliohje system-promptissa)
                </span>
              )}
              <span style={{ fontWeight: 400, color: '#666', marginLeft: 8, textTransform: 'none' }}>
                {result.model} &middot; {result.usage?.input_tokens} in / {result.usage?.output_tokens} out &middot; {result.stopReason}
              </span>
            </ResultLabel>
            <ResultBox style={{ borderColor: 'rgba(34, 197, 94, 0.2)', maxHeight: 300 }}>
              {result.response}
            </ResultBox>
          </ResultSection>
        )}
      </Content>
    </Container>
  );
};

export default PromptTestPanel;
